import { Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from '@mui/material';
import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { CourseApi, ICourse } from '../api/CourseApi';
import { CourseOfferingApi, ICourseOffering } from '../api/CourseOfferingApi';
import { handleErrorMessage } from '../component/ErrorDialog';
import AppBarContext from '../context/AppBarContext';

interface CourseFormProps {
    courseId: number;
    onSubmit: (updatedCourse: ICourse) => void;
    onDelete: (courseId: number) => void;
    isSavingOrDeleting: boolean;
}

const CourseForm: React.FC<CourseFormProps> = ({ courseId, onSubmit, onDelete, isSavingOrDeleting }) => {

    const { setErrorMessage } = React.useContext(AppBarContext)
    const history = useHistory();
    const location = useLocation();

    const [id, setId] = React.useState(-1);
    const [name, setName] = React.useState('');
    const [description, setDescription] = React.useState('');
    const [isHidden, setIsHidden] = React.useState(false);
    const [orderIndex, setOrderIndex] = React.useState(0);
    const [showConfirmation, setShowConfirmation] = React.useState(false);
    const [courseOfferingList, setCourseOfferingList] = React.useState<ICourseOffering[]>([]);

    React.useEffect(() => {
        CourseApi.getById(courseId)
            .then((response) => {
                const course = response.data;
                setId(course.id);
                setName(course.name);
                setDescription(course.description ? course.description : '');
                setOrderIndex(course.order_index);
                setIsHidden(course.is_hidden)
            })
            .catch(err => {
                handleErrorMessage(err, setErrorMessage, history, location);
            })
        CourseOfferingApi.getAll()
            .then((response) => {
                const offeringList = response.data;
                const filteredList = offeringList.filter((offering) => offering.course_id == courseId)
                setCourseOfferingList(filteredList)
            })
            .catch(err => {
                handleErrorMessage(err, setErrorMessage, history, location);
            })
    }, [courseId, history, location, setErrorMessage]);

    const handleIdChange = (e: React.ChangeEvent<HTMLInputElement>) => { setId(Number(e.target.value)); };
    const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => { setName(e.target.value); };
    const handleDescriptionChange = (e: React.ChangeEvent<HTMLInputElement>) => { setDescription(e.target.value); };
    const handleHiddenChange = (e: React.ChangeEvent<HTMLInputElement>) => { setIsHidden(e.target.checked); };
    const handleOrderIndexChange = (e: React.ChangeEvent<HTMLInputElement>) => { setOrderIndex(Number(e.target.value)); };

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const updatedCourse: ICourse = {
            id,
            order_index: orderIndex,
            name,
            description,
            is_hidden: isHidden
        };
        onSubmit(updatedCourse);
    };


    const handleDeleteConfirmation = () => {
        setShowConfirmation(true);
    };

    const handleDeleteCancel = () => {
        setShowConfirmation(false);
    };

    const handleDeleteConfirm = () => {
        setShowConfirmation(false);
        onDelete(courseId);
    };

    return (
        <form onSubmit={handleSubmit}>
            <Stack spacing={4} sx={{ width: '90%', margin: '0 auto', pt: 2, pb: 4 }}>
                <Typography variant="h6">
                    Edit Course
                </Typography>
                <input type="hidden" value={id} onChange={handleIdChange} />
                <TextField
                    label="Name"
                    value={name}
                    onChange={handleNameChange}
                    required
                />
                <TextField
                    label="Description"
                    value={description}
                    onChange={handleDescriptionChange}
                    multiline
                    minRows={3}
                    fullWidth
                />
                <div>
                    <FormControlLabel
                        control={<Checkbox checked={isHidden} onChange={handleHiddenChange} />}
                        label="Hidden"
                    />
                    {isHidden && (
                        <Typography sx={{ color: 'grey.500', fontSize: '0.9rem' }}>
                            Even though hidden, this course will still display to students enrolled in the course offerings below.
                        </Typography>
                    )}
                </div>
                <div>
                    <Typography sx={{ fontWeight: 'bold' }}>
                        Course Offerings
                    </Typography>
                    {courseOfferingList.length > 0 ? (<>
                        <TableContainer component={Paper} sx={{ maxWidth: 700, mt: 2 }}>
                            <Table size="small" aria-label="course offering table">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>ID</TableCell>
                                        <TableCell>Name</TableCell>
                                        <TableCell>Start Date</TableCell>
                                        <TableCell>End Date</TableCell>
                                        <TableCell>Description</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {courseOfferingList.map((offering) => (
                                        <TableRow key={offering.id}>
                                            <TableCell>{offering.id}</TableCell>
                                            <TableCell>{offering.name}</TableCell>
                                            <TableCell>{offering.start_date != null ? offering.start_date.toLocaleDateString() : ''}</TableCell>
                                            <TableCell>{offering.end_date != null ? offering.end_date.toLocaleDateString() : ''}</TableCell>
                                            <TableCell>{offering.description}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </>) : (<>
                        None
                    </>)}
                </div>
                <TextField
                    label="Sequence Number"
                    value={orderIndex}
                    onChange={handleOrderIndexChange}
                    type="number"
                    required
                />
                <br />
                <Button disabled={isSavingOrDeleting} type="submit" variant="contained">
                    {isSavingOrDeleting ? <CircularProgress size={24} /> : 'Save'}
                </Button>
                <Button disabled={isSavingOrDeleting} variant="contained" color="error" onClick={handleDeleteConfirmation}>
                    {isSavingOrDeleting ? <CircularProgress size={24} /> : 'Delete'}
                </Button>
            </Stack>
            <Dialog open={showConfirmation} onClose={handleDeleteCancel}>
                <DialogTitle><Typography variant="h4" sx={{ color: '#383838' }}>Delete Course</Typography></DialogTitle>
                <DialogContent>
                    <Typography sx={{ color: '#8D8D8D' }}>Are you sure you want to delete this course?</Typography>
                    <Typography>{name}</Typography>
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'space-between', '& .MuiButton-root': { flex: 1 } }}>
                    <Button onClick={handleDeleteCancel} sx={{ color: '#787878' }}>Cancel</Button>
                    <Button onClick={handleDeleteConfirm} sx={{ color: '#787878' }} autoFocus>Delete</Button>
                </DialogActions>
            </Dialog>
        </form>
    );
};

export default CourseForm;
